import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    EmailField,
    BooleanField,
    DateField,
    ReferenceField,
    EditButton,
    Filter,
    TextInput,
    BooleanInput,
    ReferenceInput,
    SelectInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    FunctionField,
    useListContext,
    RecordContextProvider,
    useGetIdentity,
} from 'react-admin';
import { Chip, Box, Typography, Fab, useMediaQuery, useTheme } from '@mui/material';
import { CheckCircle, Cancel, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const RfidUsersFilter = (props: any) => {
    const translate = useTranslate();
    const { data: identity } = useGetIdentity();
    // Vendor-scope: restrict the vendor filter dropdown to the actor's assigned
    // vendors so a scoped admin can't enumerate other merchants by name.
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;

    return (
        <Filter {...props}>
            <TextInput
                label={translate('ra.action.search')}
                source="q"
                alwaysOn
            />
            <ReferenceInput
                label={translate('resources.rfid-users.fields.vendorId')}
                source="vendorId"
                reference="vendors"
                filter={isVendorScoped ? { id: actorVendorIds } : undefined}
            >
                <SelectInput optionText="name" />
            </ReferenceInput>
            <BooleanInput
                label={translate('resources.rfid-users.fields.isActive')}
                source="isActive"
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/rfid-users/${record.id}`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {[record.firstName, record.lastName].filter(Boolean).join(' ') || record.displayName || '-'}
                            </Typography>
                            <Chip
                                icon={record.isActive ? <CheckCircle /> : <Cancel />}
                                label={record.isActive ? 'Active' : 'Inactive'}
                                color={record.isActive ? 'success' : 'default'}
                                size="small"
                                sx={{ flexShrink: 0, '& .MuiChip-icon': { fontSize: '1rem' } }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {record.email && (
                                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                                    {record.email}
                                </Typography>
                            )}
                            {record.phone && (
                                <Typography variant="caption" color="text.secondary">
                                    {record.phone}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const RfidUsersList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<RfidUsersFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': { margin: 0 },
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        rowClick="edit"
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 600,
                            },
                            '& .RaDatagrid-row': {
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField source="firstName" label={translate('resources.rfid-users.fields.firstName')} />
                        <TextField source="lastName" label={translate('resources.rfid-users.fields.lastName')} />
                        <EmailField source="email" label={translate('resources.rfid-users.fields.email')} />
                        <TextField source="phone" label={translate('resources.rfid-users.fields.phone')} />
                        <ReferenceField
                            source="vendorId"
                            reference="vendors"
                            label={translate('resources.rfid-users.fields.vendorName')}
                        >
                            <TextField source="name" />
                        </ReferenceField>
                        <FunctionField
                            label={translate('resources.rfid-users.fields.isActive')}
                            render={(record: any) => (
                                <Chip
                                    icon={record.isActive ? <CheckCircle /> : <Cancel />}
                                    label={record.isActive ? translate('ra.boolean.true') : translate('ra.boolean.false')}
                                    color={record.isActive ? 'success' : 'default'}
                                    size="small"
                                    sx={{
                                        '& .MuiChip-icon': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />
                            )}
                        />
                        <DateField
                            source="createdAt"
                            label={translate('resources.rfid-users.fields.createdAt')}
                            showTime
                        />
                        <EditButton />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/rfid-users/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
